import React, { lazy, useEffect, Suspense } from "react";
import { useInitTracking } from "@configs/tracking";
import { Routes, Route, Navigate } from "react-router-dom";
import { MaintenanceGuard, ProtectedGuard, GuestGuard } from "./Guards";
import * as routesPaths from "@constants/routes";
import * as Sentry from "@sentry/react";
// Páginas principais e critica coloca no import direto para carregar mais rapido, caso seja uma página secundária, coloca no lazy load

//##### IMPORT DIRETO - páginas críticas da aplicação #####
import FlowLogin from "@features/FlowLogin";
import Purchase from "@features/Purchase";
import Manut from "@features/ManutPage/ManutPage";
import Registry from "@components/Registry/CardSignUp/CardSignUp";
import ModularDashboard from "@features/ModularDashboard";
import InviteCode from "@features/InviteCode";
import RescueCode from "@features/InviteCode/RescueCode";
import RegisterSuccessScreen from "@components/Registry/RegistrySteps/components/RegisterSuccessScreen";
import Layout from "../hoc/Layout/Layout/Layout";
import { CircularProgress, Box, Typography } from "@mui/material";

//##### LAZY LOAD - páginas pesadas e secundarias #####
const MyAccount = lazy(() => import("@features/MyAccount"));
const AccountSettings = lazy(() => import("@features/AccountSettings"));
const Activities = lazy(() => import("@features/NewActivities"));
const Achievements = lazy(() => import("@features/Achievements/Achievements"));
const ActInfo = lazy(() => import("@features/Activities/ActivityInfo"));
const Bot = lazy(() => import("@features/BotAfinando"));
const ChangeUser = lazy(() => import("@features/ChangeUser"));
const CoinStore = lazy(() => import("@features/CoinStore"));
const GameRanking = lazy(() => import("@features/GameRanking/GameRanking"));
const LegalInfo = lazy(() => import("@components/LegalInfo/LegalInfo"));
const ManagePlan = lazy(() => import("@features/MyAccount/ManagePlan"));
const MoreActivitiesCategory = lazy(
  () =>
    import("@features/NewActivities/CategoryContainer/MoreActivitiesCategory.Mobile"),
);
const Progress = lazy(() => import("@features/NewProgress/Index"));
const PurchaseHistoryPage = lazy(
  () => import("@features/MyAccount/PurchaseHistoryPage/Index.jsx"),
);

const TakeTest = lazy(
  () => import("@features/PersonalizedTraining/pages/TakeTest/TakeTest"),
);
const TakeTestPlan = lazy(
  () => import("@features/PersonalizedTraining/pages/TakeTestPlan/TakeTestPlan"),
);

const UserManagement = lazy(() => import("@features/UserManagement"));

export default function AppRoutes({
  isMaintenance,
  isAuthenticated,
  session,
  myUsers,
  isLoading,
  check_cache_local,
}) {
  
  // effect pra ativar o sentry. isolado das rotas
  useEffect(() => {
    if (
      isAuthenticated &&
      process.env.REACT_APP_STAGE === "prod" &&
      session?.my_user
    ) {
      Sentry.setUser({
        id: session.my_user.sub,
        email: session.my_user.email,
        profile: session.my_user.profile,
      });
    }
  }, [isAuthenticated, session]);

  // Lógicas de regras de negócio específicas para permissões de tela
  const isPersonalUser = session?.my_user?.profile === "personal";
  const isProfessionalUser = session?.my_user?.profile === "professional";
  const hasNoChildren = !myUsers || myUsers.length === 0;
  const shouldShowPersonalizedTest = isPersonalUser && hasNoChildren;
  useInitTracking(session);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          zIndex: 9999,
        }}
      >
        <CircularProgress size={60} thickness={4} color="primary" />
        <Typography
          variant="h6"
          color="textSecondary"
          sx={{ mt: 3, fontWeight: 500 }}
        >
          Carregando sessão...
        </Typography>
      </Box>
    );
  }

  return (
    <Suspense fallback={<div>Carregando página...</div>}>
      <Routes>
        {/* ROTA DE MANUTENÇÃO (Fora dos filtros globais) */}
        <Route
          path={routesPaths.R_MANUT}
          element={
            isMaintenance === true || isMaintenance === "true" ? (
              <Manut />
            ) : (
              <Navigate
                to={
                  isAuthenticated
                    ? routesPaths.R_DASHBOARD
                    : routesPaths.R_LOGIN
                }
                replace
              />
            )
          }
        />

        {/* ENVELOPE GLOBAL: Se estiver em manutenção, nada abaixo disso roda */}
        <Route element={<MaintenanceGuard isMaintenance={isMaintenance} />}>
          {/* ================= ROTAS PÚBLICAS EXCLUSIVAS (Usuário deslogado apenas) ================= */}
          <Route element={<GuestGuard isAuthenticated={isAuthenticated} />}>
            <Route
              path={routesPaths.R_ROOT}
              element={<Navigate to={routesPaths.R_LOGIN} replace />}
            />
            <Route path={routesPaths.R_LOGIN} element={<FlowLogin />} />
            <Route path={`${routesPaths.R_SIGNUP}/*`} element={<Registry />} />
          </Route>
          {/* ================= ROTAS HÍBRIDAS (Acessíveis por LOGADOS e DESLOGADOS) ================= */}
          <Route
            path="/cadastro/:profile/dados/informacoes/codigo/obrigado"
            element={<RegisterSuccessScreen />}
          />
          <Route
            path={routesPaths.R_REGISTER_SUCCESS}
            element={<RegisterSuccessScreen />}
          />
          <Route element={<Layout check_cache_local={check_cache_local} />}>
            <Route path={routesPaths.R_LEGAL_INFO} element={<LegalInfo />} />
            <Route
              path={routesPaths.R_ACTIVTS}
              element={<Activities isAuthenticated={isAuthenticated} />}
            />
            <Route
              path={routesPaths.R_ACTIVTS_CATEGORY}
              element={<MoreActivitiesCategory />}
            />

            <Route path={routesPaths.R_ACTIVTS_INFO} element={<ActInfo />} />
          </Route>
          {/* ================= ROTAS PRIVADAS EXCLUSIVAS (Usuário logado apenas) ================= */}
          <Route element={<ProtectedGuard isAuthenticated={isAuthenticated} />}>
            <Route element={<Layout check_cache_local={check_cache_local} />}>
              <Route
                path={routesPaths.R_LIST_MY_USERS}
                element={<UserManagement />}
              />
              <Route
                path={routesPaths.R_SELECT_USER}
                element={<ChangeUser />}
              />
              <Route
                path={routesPaths.R_INVITE_USER}
                element={<InviteCode />}
              />
              <Route
                path={routesPaths.R_RESCUE_CODE}
                element={<RescueCode />}
              />
              <Route
                path={`${routesPaths.R_COINSTORE}/*`}
                element={<CoinStore />}
              />
              <Route
                path={`${routesPaths.R_PROGRESS}/*`}
                element={<Progress />}
              />
              <Route
                path={`${routesPaths.R_ACHIEVEMENTS}/*`}
                element={<Achievements />}
              />
              <Route path={`${routesPaths.R_BUY}/*`} element={<Purchase />} />
              <Route path={routesPaths.R_BOT_AFINANDO} element={<Bot />} />
              <Route
                path={routesPaths.R_MY_ACCOUNT}
                exact
                element={<MyAccount />}
              />
              <Route
                path={routesPaths.R_MY_ACCOUNT_SETTINGS}
                exact
                element={<AccountSettings />}
              />
              <Route
                path={routesPaths.R_MANAGE_PLAN}
                exact
                element={<ManagePlan />}
              />
              <Route
                path={routesPaths.R_MANAGE_PURCHASES}
                exact
                element={<PurchaseHistoryPage />}
              />

              <Route
                path={`${routesPaths.R_DASHBOARD}/*`}
                element={<ModularDashboard />}
              />

              {/* Rotas Condicionais baseadas no Perfil (Regra de Negócio) */}
              {(isPersonalUser || isProfessionalUser) && (
                <Route
                  path={routesPaths.R_MODULAR_DASHBOARD}
                  element={<ModularDashboard />}
                />
              )}

              {/* Se a regra de negócio exigir que o teste personalizado só apareça para logados */}
              {shouldShowPersonalizedTest && (
                <>
                  <Route
                    path={routesPaths.R_TAKE_TEST}
                    element={<TakeTest />}
                  />
                  <Route
                    path={routesPaths.R_TAKE_TEST_PLAN}
                    element={<TakeTestPlan />}
                  />
                </>
              )}

              <Route
                path={routesPaths.R_GAME_RANKING}
                element={<GameRanking />}
              />
            </Route>
          </Route>
        </Route>

        {/* ROTA PADRÃO (404 / REDIRECIONAMENTO GLOBAL) */}
        <Route
          path="*"
          element={
            isAuthenticated ? (
              <Navigate to={routesPaths.R_DASHBOARD} replace />
            ) : (
              <Navigate to={routesPaths.R_LOGIN} replace />
            )
          }
        />
      </Routes>
    </Suspense>
  );
}
